import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createVNode as _createVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-bcc3cce8"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["aria-label", "onClick", "onKeydown"]
const _hoisted_2 = ["aria-label", "aria-selected", "onClick", "onFocus", "onKeydown"]
const _hoisted_3 = {
  key: 0,
  class: "selection-indicator"
}

import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";

import ElementTooltip from "./ElementTooltip.vue";
import NoteLabel from "./NoteLabel.vue";
import { useElementTooltip } from "@/composables/useElementTooltip";
import { useGlobalTooltip } from "@/composables/useGlobalTooltip";
import { useFretboardStore } from "@/stores/fretboard";
import { storeToRefs } from "pinia";


export default /*#__PURE__*/_defineComponent({
  __name: 'TuningControls',
  props: {
    tuning: {},
    numStrings: {},
    noteNames: {},
    isGuitar: { type: Boolean }
  },
  emits: ["tuning-changed"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// Access fretboard store for root note (key signature)
const store = useFretboardStore();
const { rootNote } = storeToRefs(store);

// Initialize element tooltip system
const elementTooltip = useElementTooltip();

// Global tooltip coordination
const {
  registerTooltip,
  unregisterTooltip,
  showTooltip: showGlobalTooltip,
  hideTooltip: hideGlobalTooltip,
} = useGlobalTooltip();

const emit = __emit;

const localTuning = ref([...props.tuning]);
const openDropdownIndex = ref<number | null>(null);
const focusedNoteIndex = ref(0);
const tuningControlsRef = ref<HTMLElement>();
const selectorRefs = ref<Record<number, HTMLElement>>({});
const dropdownRef = ref<HTMLElement>();
const transformOriginY = ref(0);

const reversedLocalTuning = computed(() => [...localTuning.value].reverse());

// Computed style for dropdown positioning and animation
const dropdownStyle = computed(() => {
  if (openDropdownIndex.value === null) return {};

  return {
    transformOrigin: `left ${transformOriginY.value}px`,
  };
});

// Tooltip functions accessed directly from props to maintain reactivity

watch(
  () => props.tuning,
  (newTuning) => {
    localTuning.value = [...newTuning];
  },
  { immediate: true }
);

function changeTuning(index: number, newValue: string) {
  const newTuning = [...localTuning.value];
  newTuning[index] = newValue;
  emit("tuning-changed", newTuning);
}

// Set selector ref for each dropdown
function setSelectorRef(index: number, el: any) {
  if (el) {
    selectorRefs.value[index] = el;
  }
}

function openSelector(index: number) {
  // Toggle behavior: if clicking the same trigger that's already open, close it
  if (openDropdownIndex.value === index) {
    closeAndReturnFocus();
    return;
  }

  // Set consistent transform origin for all dropdowns
  transformOriginY.value = 145; // Center of dropdown height (290px / 2)

  openDropdownIndex.value = index;
  const currentNote = reversedLocalTuning.value[index];
  focusedNoteIndex.value = props.noteNames.indexOf(currentNote);

  // Focus the dropdown container first, then the selected option
  nextTick(() => {
    if (dropdownRef.value) {
      dropdownRef.value.focus();

      // Then focus the selected option
      nextTick(() => {
        const selectedOption = dropdownRef.value?.querySelector(
          ".tuning-option--selected"
        ) as HTMLElement;
        if (selectedOption) {
          selectedOption.focus();
        }
      });
    }
  });
}

function getCurrentSelectedNote() {
  if (openDropdownIndex.value === null) return "";
  return reversedLocalTuning.value[openDropdownIndex.value];
}

function closeSelector() {
  openDropdownIndex.value = null;
  focusedNoteIndex.value = 0;

  // If mouse is still over the tuning controls after closing, show tooltip again
  nextTick(() => {
    if (tuningControlsRef.value?.matches(":hover")) {
      elementTooltip.showAbove("Change tuning", tuningControlsRef.value);
      showGlobalTooltip("tuning-controls");
    }
  });
}

function closeAndReturnFocus() {
  const currentIndex = openDropdownIndex.value;
  closeSelector();

  // Return focus to the trigger button
  nextTick(() => {
    if (currentIndex !== null) {
      const triggerButton = selectorRefs.value[currentIndex]?.querySelector(
        ".tuning-selector-trigger"
      ) as HTMLElement;
      if (triggerButton) {
        triggerButton.focus();
      }
    }
  });
}

function selectNote(tuningIndex: number, note: string) {
  const currentDropdownIndex = openDropdownIndex.value;
  changeTuning(tuningIndex, note);
  closeSelector();

  // Return focus to the trigger button after selection
  nextTick(() => {
    if (currentDropdownIndex !== null) {
      const triggerButton = selectorRefs.value[
        currentDropdownIndex
      ]?.querySelector(".tuning-selector-trigger") as HTMLElement;
      if (triggerButton) {
        triggerButton.focus();
      }
    }
  });
}

function handleTriggerKeydown(event: KeyboardEvent, index: number) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    openSelector(index);
  }
}

function handleDropdownKeydown(event: KeyboardEvent) {
  if (openDropdownIndex.value === null) return;

  switch (event.key) {
    case "Escape":
      event.preventDefault();
      event.stopPropagation();
      closeAndReturnFocus();
      break;

    case "Tab": {
      closeSelector();
      // Don't prevent default - let browser handle tab naturally
      break;
    }

    case "ArrowUp":
      event.preventDefault();
      event.stopPropagation();
      moveFocus(-1);
      break;

    case "ArrowDown":
      event.preventDefault();
      event.stopPropagation();
      moveFocus(1);
      break;

    case "Enter":
    case " ": {
      event.preventDefault();
      event.stopPropagation();
      const focusedNote = props.noteNames[focusedNoteIndex.value];
      if (focusedNote) {
        selectNote(props.numStrings - 1 - openDropdownIndex.value, focusedNote);
      }
      break;
    }
  }
}

function handleOptionKeydown(event: KeyboardEvent, note: string) {
  switch (event.key) {
    case "Enter":
    case " ":
      event.preventDefault();
      event.stopPropagation();
      if (openDropdownIndex.value !== null) {
        selectNote(props.numStrings - 1 - openDropdownIndex.value, note);
      }
      break;
  }
}

function moveFocus(direction: number) {
  const newIndex = Math.max(
    0,
    Math.min(props.noteNames.length - 1, focusedNoteIndex.value + direction)
  );
  focusedNoteIndex.value = newIndex;
  focusNoteAtIndex(newIndex);
}

function focusNoteAtIndex(noteIndex: number) {
  nextTick(() => {
    const noteButton = dropdownRef.value?.querySelector(
      `[aria-label*="${props.noteNames[noteIndex]}"]`
    ) as HTMLElement;
    if (noteButton) {
      noteButton.focus();
    }
  });
}

function handleClickOutside(event: MouseEvent) {
  if (openDropdownIndex.value !== null) {
    const currentSelector = selectorRefs.value[openDropdownIndex.value];
    if (currentSelector && !currentSelector.contains(event.target as Node)) {
      closeSelector();
    }
  }
}

// Tooltip handlers that position above the group
function handleTuningHover(event: MouseEvent) {
  // Only show tooltip if no dropdown is open
  if (openDropdownIndex.value === null) {
    const target = event.currentTarget as HTMLElement;
    elementTooltip.showAbove("Change tuning", target);
    showGlobalTooltip("tuning-controls");
  }
}

function handleMouseLeave() {
  // Only hide tooltip if no dropdown is open
  if (openDropdownIndex.value === null) {
    elementTooltip.hideTooltip();
    hideGlobalTooltip("tuning-controls");
  }
}

function handleTriggerFocus() {
  // Show tooltip when trigger receives focus (keyboard navigation)
  if (openDropdownIndex.value === null && tuningControlsRef.value) {
    // Show tooltip above the entire group, not individual trigger
    elementTooltip.showAbove("Change tuning", tuningControlsRef.value);
    showGlobalTooltip("tuning-controls");
  }
}

function handleTriggerBlur() {
  // Hide tooltip when focus leaves the entire tuning controls group
  setTimeout(() => {
    if (openDropdownIndex.value === null && tuningControlsRef.value) {
      // Only hide if focus has moved outside the tuning controls container
      if (!tuningControlsRef.value.contains(document.activeElement)) {
        elementTooltip.hideTooltip();
        hideGlobalTooltip("tuning-controls");
      }
    }
  }, 100);
}

// Global tooltip hide function
function hideTooltip() {
  elementTooltip.hideTooltip();
}

// Lifecycle hooks for global tooltip registration and click outside handling
onMounted(() => {
  registerTooltip("tuning-controls", hideTooltip);
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  unregisterTooltip("tuning-controls");
  document.removeEventListener("click", handleClickOutside);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "tuningControlsRef",
    ref: tuningControlsRef,
    class: _normalizeClass(["tuning-controls", { bass: _ctx.numStrings === 4 }]),
    onMouseenter: handleTuningHover,
    onMouseleave: handleMouseLeave
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(reversedLocalTuning.value, (string, index) => {
      return (_openBlock(), _createElementBlock("div", {
        key: index,
        class: _normalizeClass(["tuning-selector", { 'tuning-selector--open': openDropdownIndex.value === index }]),
        ref_for: true,
        ref: (el) => setSelectorRef(index, el)
      }, [
        _createElementVNode("button", {
          class: _normalizeClass(["tuning-selector-trigger interactive-outline", {
          'tuning-selector-trigger--active': openDropdownIndex.value === index,
          'selector-open': openDropdownIndex.value === index,
        }]),
          "aria-label": `String tuning: ${string}. Click to open tuning selector`,
          tabindex: "0",
          onClick: _withModifiers(($event: any) => (openSelector(index)), ["stop"]),
          onMousedown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
          onKeydown: ($event: any) => (handleTriggerKeydown($event, index)),
          onFocus: handleTriggerFocus,
          onBlur: handleTriggerBlur
        }, [
          _createVNode(NoteLabel, {
            "chromatic-index": _ctx.noteNames.indexOf(string),
            "display-mode": 'logical',
            "key-signature": _unref(rootNote),
            size: "small"
          }, null, 8, ["chromatic-index", "key-signature"])
        ], 42, _hoisted_1)
      ], 2))
    }), 128)),
    _createVNode(_Transition, { name: "tuning-dropdown" }, {
      default: _withCtx(() => [
        (openDropdownIndex.value !== null)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              ref_key: "dropdownRef",
              ref: dropdownRef,
              class: "tuning-selector-open",
              role: "listbox",
              "aria-label": 'Select tuning note',
              tabindex: "0",
              onKeydown: _cache[1] || (_cache[1] = ($event: any) => (handleDropdownKeydown($event))),
              style: _normalizeStyle(dropdownStyle.value)
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.noteNames, (note) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: note,
                  class: _normalizeClass(["tuning-option", {
          'tuning-option--selected': note === getCurrentSelectedNote(),
          'tuning-option--focused':
            focusedNoteIndex.value === _ctx.noteNames.indexOf(note),
        }]),
                  "aria-label": `${note} key`,
                  "aria-selected": note === getCurrentSelectedNote(),
                  role: "option",
                  onClick: ($event: any) => (selectNote(_ctx.numStrings - 1 - openDropdownIndex.value, note)),
                  onFocus: ($event: any) => (focusedNoteIndex.value = _ctx.noteNames.indexOf(note)),
                  onKeydown: ($event: any) => (handleOptionKeydown($event, note))
                }, [
                  (note === getCurrentSelectedNote())
                    ? (_openBlock(), _createElementBlock("div", _hoisted_3))
                    : _createCommentVNode("", true),
                  _createVNode(NoteLabel, {
                    "chromatic-index": _ctx.noteNames.indexOf(note),
                    "display-mode": 'logical',
                    "key-signature": _unref(rootNote),
                    size: "medium"
                  }, null, 8, ["chromatic-index", "key-signature"])
                ], 42, _hoisted_2))
              }), 128))
            ], 36))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(ElementTooltip, {
      visible: _unref(elementTooltip).state.value.visible,
      content: _unref(elementTooltip).state.value.content,
      "target-element": _unref(elementTooltip).state.value.targetElement,
      position: _unref(elementTooltip).state.value.position
    }, null, 8, ["visible", "content", "target-element", "position"])
  ], 34))
}
}

})